import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faInbox } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { HttpError } from 'src/app/core/http-error';
import { BoxGroup } from 'src/app/models/box-group';
import { Rights } from 'src/app/models/enums/rights';
import { IBoxGroup } from 'src/app/models/interfaces/box-group.interface';
import { BoxGroupsService } from 'src/app/services/box-groups.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { FormMode } from '../../controls/common/form-mode';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { BoxDetailContext } from './box-detail-form-context';
import { BoxGroupDetailComponent } from './box-group-detail/box-group-detail.component';
import { BoxGroupsListComponent } from './box-groups-list/box-groups-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-box-groups',
    templateUrl: './box-groups.component.html',
    styleUrls: ['./box-groups.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, BoxGroupsListComponent, DetailHeaderComponent, BoxGroupDetailComponent],
})
export class BoxGroupsComponent implements OnInit {
    /**
     * Reference au composant BoxesList.
     */
    @ViewChild(BoxGroupsListComponent) boxGroupsList: BoxGroupsListComponent;

    /**
     * Liste des icônes utilisés dans le template
     */
    public faInbox = faInbox;

    /**
     * Liste des enums utilisés dans le template
     */
    public FormMode = FormMode;
    public DisplayMode = DisplayMode;

    /**
     * Contexte du formulaire.
     */
    public context: BoxDetailContext = null;

    /**
     * Mode d'affichage de la liste (Liste, Arborescence, formulaire)
     */
    public displayMode = DisplayMode.List;

    /**
     * Indique si le bouton d'ajout/Edition doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     *
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param boxGroupsService : Service métier utilisé pour gérer les groupes de bannettes.
     */
    constructor(
        protected notificationService: NotificationService,
        private boxGroupsService: BoxGroupsService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {}

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.BoxCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.BoxEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un groupe de bannettes.
     * @param boxGroup Groupe de bannettes selectionné.
     */
    public onBoxGroupSelected(boxGroup: BoxGroup) {
        this.createContext();
        this.loadBoxGroup(boxGroup.id).subscribe((x) => {
            this.refresh(BoxGroup.createFormCopy(x), FormMode.Reading);
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param boxGroup Groupe de bannettes sélectionné.
     */
    public onBoxGroupDisplayed(boxGroup: BoxGroup) {
        this.createContext();

        this.loadBoxGroup(boxGroup.id).subscribe((x) => {
            this.refresh(BoxGroup.createFormCopy(x), FormMode.Reading);
        });
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param user Groupe de bannettes sélectionné.
     */
    public onBoxGroupEdited(boxGroup: BoxGroup) {
        this.createContext();

        // Cas de la mise à jour.
        this.loadBoxGroup(boxGroup.id).subscribe((x) => {
            this.refresh(BoxGroup.createFormCopy(x), FormMode.Edition);
        });
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param box : Valeur du groupe de bannettes retourné par le formulaire.
     */
    public onBoxGroupUpdated(boxGroup: BoxGroup): void {
        if (this.context.editedBoxGroupId) {
            // Cas de la mise à jour.
            this.boxGroupsService.update(this.context.editedBoxGroupId, boxGroup).subscribe({
                next: (x) => {
                    this.refreshWithNotification(BoxGroup.createFormCopy(x), 'Groupe de bannettes mis à jour', FormMode.Reading);
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le groupe de bannettes.' + error.message);
                    this.spinner.hide('main-spinner');
                },
            });
        } else {
            // Cas de la création.
            this.boxGroupsService.add(boxGroup).subscribe({
                next: (x) => {
                    this.refreshWithNotification(BoxGroup.createFormCopy(x), 'Groupe de bannettes créé', FormMode.Reading);
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer le groupe de bannette.' + error.message);
                    this.spinner.hide('main-spinner');
                },
            });
        }
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onBoxGroupCreated() {
        this.displayForm(BoxGroup.createFormCopy(new BoxGroup()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param boxGroup Groupe de bannettes sélectionné.
     */
    public onBoxGroupDeleted(boxGroup: BoxGroup) {
        // Suppression effective du groupe de bannettes.
        this.boxGroupsService.delete(boxGroup.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Groupe de bannettes supprimé');
                this.boxGroupsList.refresh();
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'La suppression du groupe de bannettes a échoué.');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onBoxGroupsExported() {
        this.createContext();
        this.boxGroupsList.export('GroupesDeBannettes.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onBoxGroupsImported() {
        this.boxGroupsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onBoxGroupsHelped() {
        this.boxGroupsList.displayHelpDialog('boxgroups');
    }

    public onBoxGroupsSorted(boxGroups: BoxGroup[]) {
        this.boxGroupsService.applyOrderChanges(boxGroups).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, "L'ordre des groupes de bannettes a été appliqué");
                this.boxGroupsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible dappliquer l'ordre des groupes de bannettes." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
	/**
	 * Définit le mode d'affichage de la page (Form / List).
	 * Exemple : si le mode est à List, l'appel de setDisplayMode(DisplayMode.Form) affichera le formulaire mais un click sur le header 'Groupes d'utilisateurs' affichera la liste.
	 * @param mode Mode d'affichage
	 */
    public setDisplayMode(mode: DisplayMode) {
        this.displayMode = mode;
    }

    /**
     * Charge les informations d'un groupe de bannettes.
     * Il s'agit des données affichées dans l'onglet Infos du détail.
     * @param boxGroupId : Id du groupe de bannettes à charger.
     * @returns Les informations du groupe de bannettes.
     */
    public loadBoxGroup(boxGroupId: string): Observable<BoxGroup> {
        return this.boxGroupsService.get(boxGroupId);
    }

    /**
     * Affiche Les données d'un groupe de bannettes dans le formulaire.
     * @param boxGroup : Instance du groupe de bannettes à afficher dans le formulaire.
     * @param formMode Mode d'affichage du formulaire.
     */
    displayForm(boxGroup: IBoxGroup, formMode: FormMode): void {
        let context = this.fillForm(boxGroup);
        context.boxGroupContext.formVisible = true;
        context.formMode = formMode;

        this.context = context;

        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param boxGroup : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    public fillForm(boxGroup: IBoxGroup): BoxDetailContext {
        let context = this.createContext();
        context.boxGroupContext.item = boxGroup;
        context.editedBoxGroupId = boxGroup.id;
        return context;
    }

    /**
     * Rafraichi la page.
     * @param boxGroup : Valeur du client retourné par le formulaire.
     * @param mode : Mode d'affichage.
     */
    public refresh(boxGroup: IBoxGroup, mode: FormMode): void {
        this.boxGroupsList.refresh();
        this.displayForm(boxGroup, mode);
        this.fillForm(boxGroup);
    }

    /**
     * Rafraichi la page.
     * @param boxGroup : Valeur du client retourné par le formulaire.
     * @param notificationString: Texte de la notification.
     * @param mode : Mode d'affichage.
     */
    public refreshWithNotification(boxGroup: IBoxGroup, notificationString: string, mode: FormMode): void {
        this.refresh(boxGroup, mode);
        this.notificationService.displaySuccess(null, notificationString);
        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Créer un nouveau contexte.
     */
    private createContext(): BoxDetailContext {
        let context = new BoxDetailContext();
        return context;
    }
}
